'use client'

import Loading from '@/components/circle-loading'
import PlaceBidLabel from './place-bid-label'
import OfferUsd from './offer-usd'
import BitcoinIcon from '@/icons/bitcoin-filled'
import SelectButton from '@/components/search-input/components/select-button'
import { seedConfirmedCollectionOffer } from '@/utils/offer-book'
import { NAT_COLLECTION, natTerms, btcSats, natBidTotal, formatNatAmount } from '@/utils/nat-offers'
import { offerUsd } from '@/utils/offer-currency'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { fetchLiveItemOutpoint } from '@/utils/offer-item'
import { toast } from '@/components/ui/use-toast'
import * as Dialog from '@radix-ui/react-dialog'
import { ArrowUpDown, Bitcoin, X } from 'lucide-react'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import useCollectionAttributes from '@/utils/hooks/useCollectionAttributes'
import event from '@/utils/event'
import { ensureEccLib } from '@/utils/wallet'
import {
	makeOffer,
	makeOfferBatch,
	MAX_OFFER_QUANTITY,
	MAX_OFFER_MESSAGE_LENGTH,
	OfferBatchError,
	OfferError,
	offerSocialPostEnabled,
	type OfferStep,
	type Offer,
	type OfferScope,
	MIN_OFFER_SATS,
	MIN_OFFER_BTC,
	BID_REBATE_PERCENT,
	BID_REBATE_BPS,
	bidFeeSplit
} from '@/utils/offers'
import { API_URL, MARKET_FEE_PERCENTAGE } from '@/utils/constants'
import styles from './offer-dialog.module.css'
import OfferSuccessReceipt from './offer-success-receipt'
import {
	beginOfferFlow,
	OFFER_STEP_STAGES,
	type OfferTelemetrySurface
} from '@/utils/offer-telemetry'

/** How far through one offer each step is, for the active progress segment. */
const STEP_FRACTION: Record<OfferStep, number> = {
	building: 0.08,
	sign_funding: 0.25,
	preparing: 0.5,
	sign_offer: 0.72,
	activating: 0.9,
	done: 1
}

const STEP_COPY: Record<OfferStep, string> = {
	building: 'Preparing your offer…',
	sign_funding: 'Sign the funding transaction in your wallet',
	preparing: 'Building the offer escrow…',
	sign_offer: 'Sign the offer in your wallet',
	activating: 'Publishing your offer…',
	done: 'Offer placed'
}
import OfferPriceShortcuts, { offerInputBtc, validReferencePrice } from './offer-price-shortcuts'
import useBidReference from '@/utils/hooks/useBidReference'
import useSponsoredPost from '@/app/alkanes/lib/use-sponsored-post'
import { collectionSmartTag } from '@/app/alkanes/lib/collection-tag'

/** Size of the DIESEL mint output on every offer transaction; mirrors the API's offer_mint_vbytes. */
const OFFER_MINT_VBYTES = 41

const fmtBtc = (sats: number) => (sats / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })
interface Trait {
	trait_type: string
	values: { value: string; count: number }[]
}

function WalletWaitHint() {
	const [waiting, setWaiting] = useState(false)
	useEffect(() => {
		const timer = setTimeout(() => setWaiting(true), 10_000)
		return () => clearTimeout(timer)
	}, [])
	return waiting ? (
		<span className={styles.hint}>
			<br />
			Your wallet is still preparing the signing request. Keep it unlocked; you can cancel below.
		</span>
	) : null
}

/**
 * The message the buyer reads when a bid fails. Wallet extensions reject
 * with plain objects, not Errors (UniSat: `{ code: 4001, message }`), so
 * read `message` off anything that carries one instead of falling back to
 * the generic line that tells them nothing.
 */
export function offerFailureMessage(e: unknown): string {
	if (e instanceof OfferError || e instanceof Error)
		return e.message || 'The offer could not be placed.'
	const message = (e as { message?: unknown } | null)?.message
	if (typeof message === 'string' && message.trim()) {
		return /reject|cancel|denied/i.test(message)
			? 'The wallet did not sign. Nothing was placed; try again when you are ready.'
			: message
	}
	if (typeof e === 'string' && e.trim()) return e
	return 'The offer could not be placed.'
}

export default function MakeOfferModal({
	open,
	onClose,
	itemPreview,
	receiptPreview,
	collectionIcon,
	inscriptionId,
	collectionSlug,
	initialScope,
	initialPriceSats,
	initialTrait,
	name,
	reference,
	traitOffers = [],
	supply,
	onPlaced,
	natEnabled = false,
	surface = 'offer_dialog'
}: {
	natEnabled?: boolean
	/** Where the dialog was opened from, for failure telemetry. */
	surface?: OfferTelemetrySurface
	open: boolean
	onClose: () => void
	itemPreview?: React.ReactNode
	receiptPreview?: React.ReactNode
	collectionIcon?: React.ReactNode
	inscriptionId?: string
	collectionSlug?: string
	initialPriceSats?: number
	initialTrait?: { type: string; value: string }
	initialScope?: OfferScope
	name?: string | null
	reference?: {
		listedPriceSats?: number | null
		floorSats?: number | null
		/** The fair-value mark when the caller already has it; the modal fetches it otherwise. */
		fairSats?: number | null
		topOfferSats?: number | null
	}
	supply?: number
	traitOffers?: Offer[]
	onPlaced?: (offer: Offer) => void
}) {
	// Warm the cached signing module while the user fills the form; no wallet
	// request, account prompt, or transaction happens here.
	useEffect(() => {
		if (open) void ensureEccLib().catch(() => {})
	}, [open])
	const { mutate } = useSWRConfig()
	const [placedOffer, setPlacedOffer] = useState<Offer | null>(null)
	const [scope, setScope] = useState<OfferScope>(
		initialScope ?? (inscriptionId ? 'item' : 'collection')
	)
	const [trait, setTrait] = useState<{ type: string; value: string } | null>(initialTrait ?? null)
	const [search, setSearch] = useState('')
	const [message, setMessage] = useState('')
	const socialPostOn = offerSocialPostEnabled()
	const {
		attributes,
		isLoading: traitsLoading,
		error: traitsError
	} = useCollectionAttributes(open && scope === 'trait' ? collectionSlug : undefined)
	const groups = useMemo(
		() =>
			(attributes as Trait[])
				.map((group) => ({
					...group,
					values: [...group.values]
						.sort((a, b) => b.count - a.count)
						.filter((value) =>
							`${group.trait_type} ${value.value}`
								.toLowerCase()
								.includes(search.toLowerCase().trim())
						)
				}))
				.filter((group) => group.values.length),
		[attributes, search]
	)
	const isNat = collectionSlug === NAT_COLLECTION && !inscriptionId
	const loggedIn = useLoggedIn()
	const { data: rates, isLoading: feesLoading, isFallback: feesFallback } = useFeeEstimates()
	const { exchangeRate } = useExchangeRate()
	const [enteredAmount, setEnteredAmount] = useState<string | null>(
		validReferencePrice(initialPriceSats) ? offerInputBtc(initialPriceSats) : null
	)
	// Collection and trait bids (and item bids on unlisted items) anchor to the
	// fair-value mark; the floor only when no mark exists.
	const bidRef = useBidReference(
		open && collectionSlug && !isNat ? collectionSlug : null,
		reference?.floorSats
	)
	const referenceSats =
		scope === 'item' && validReferencePrice(reference?.listedPriceSats)
			? reference.listedPriceSats
			: bidRef.sats ?? reference?.floorSats
	const referenceLabel: 'List price' | 'Floor price' =
		scope === 'item' && validReferencePrice(reference?.listedPriceSats)
			? 'List price'
			: bidRef.label
	// User edits, including clearing the field, take precedence over refreshed prices.
	const raw =
		enteredAmount ?? (validReferencePrice(referenceSats) ? offerInputBtc(referenceSats) : '')
	const [amountCurrency, setAmountCurrency] = useState<'BTC' | 'USD'>('BTC')
	const [usdDraft, setUsdDraft] = useState('')
	const [tokenAmount, setTokenAmount] = useState('')
	const [unitPrice, setUnitPrice] = useState('')
	const [quantityRaw, setQuantityRaw] = useState('1')
	const [currentOffer, setCurrentOffer] = useState(1)
	const [batchStopped, setBatchStopped] = useState(false)
	// Offers of the running batch that are live, for the progress bar and the one summary toast.
	const [placedCount, setPlacedCount] = useState(0)
	const batchPlaced = useRef<Offer[]>([])
	const progressRef = useRef<HTMLDivElement>(null)
	// Bids stay live until the buyer cancels; the escrow's own recovery window is a year.
	const days = 365
	const social = useSponsoredPost()
	const socialPosted = useRef(false)
	const [speed, setSpeed] = useState('normal')
	const [customFee, setCustomFee] = useState('')
	const [connectingWallet, setConnectingWallet] = useState(false)
	const [attempted, setAttempted] = useState(false)
	const validationTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const submitRef = useRef<HTMLButtonElement>(null)
	const feeDetailsRef = useRef<HTMLDetailsElement>(null)
	const [step, setStep] = useState<OfferStep | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [itemMoved, setItemMoved] = useState(false)
	const statusRef = useRef<HTMLParagraphElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const dialogRef = useRef<HTMLDivElement>(null)
	const titleRef = useRef<HTMLHeadingElement>(null)
	const returnFocusRef = useRef<HTMLElement | null>(null)
	const submitting = useRef(false)
	const operation = useRef<AbortController | null>(null)
	const close = () => {
		operation.current?.abort()
		onClose()
	}
	useEffect(() => {
		if (!open) operation.current?.abort()
	}, [open])
	const isItem = scope === 'item'
	// An item offer is funded against the item's live output; the flow reads
	// it again past every cache, but submit waits for the first read so a
	// lookup that fails is a retry here, not a failed offer.
	const {
		data: liveItem,
		error: liveItemError,
		mutate: reloadLiveItem
	} = useSWR(
		open && isItem && inscriptionId ? ['offers:live-item', inscriptionId] : null,
		([, id]) => fetchLiveItemOutpoint(id),
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	)
	const itemPending = isItem && !!inscriptionId && !liveItem
	const refreshItem = () => {
		setError(null)
		setItemMoved(false)
		void reloadLiveItem()
		if (inscriptionId) {
			void mutate(`${API_URL}/inscription/${inscriptionId}`)
			void mutate(`${API_URL}/inscription/${inscriptionId}/outpoint`)
		}
	}
	const quantity = isItem || isNat ? 1 : Number(quantityRaw)
	const validQuantity =
		Number.isInteger(quantity) && quantity >= 1 && quantity <= MAX_OFFER_QUANTITY
	const speeds = {
		slow: Math.max(1, Math.round(rates?.hourFee ?? 1)),
		normal: Math.max(1, Math.round(rates?.halfHourFee ?? 2)),
		fast: Math.max(1, Math.round(rates?.fastestFee ?? 3))
	}
	const feeRate = speed === 'custom' ? Number(customFee) : speeds[speed as keyof typeof speeds]
	// The settlement budget is locked into the escrow and cannot be bumped
	// later. Without a live estimate the presets are the 1 sat/vB fallback,
	// so only a rate the buyer typed may go in.
	const feeEstimatesLoading = speed !== 'custom' && feesLoading
	const feeEstimatesMissing = speed !== 'custom' && !feesLoading && feesFallback
	const feesUnavailable = feeEstimatesLoading || feeEstimatesMissing
	const switchToCustomFee = () => {
		setSpeed('custom')
		if (feeDetailsRef.current) feeDetailsRef.current.open = true
		requestAnimationFrame(() => document.getElementById('offer-custom-fee')?.focus())
	}
	const numericSats = isNat ? natBidTotal(tokenAmount, unitPrice) ?? 0 : btcSats(raw)
	const priceSats = Number.isSafeInteger(numericSats) && numericSats > 0 ? numericSats : 0
	// Exactly the API's legacy_price_split: the 2.7% fee grossed up from the
	// price and truncated, with the 1,000-sat minimum tested on the gross.
	const marketRate = Math.round(MARKET_FEE_PERCENTAGE * 1000) / 100_000
	const grossSats = priceSats + Math.floor((priceSats * marketRate) / (1 - marketRate))
	const fullMarketFee =
		priceSats > 0
			? isNat
				? Math.max(Number((BigInt(priceSats) * BigInt(270)) / BigInt(9730)), 1000)
				: grossSats * marketRate < 1000
				? 1000
				: grossSats - priceSats
			: 0
	// Bids earn a rebate: the buyer funds the standard fee minus 1.69% of the bid.
	const { rebate: bidRebate, fee: marketFee } = bidFeeSplit(priceSats, fullMarketFee)
	// The rebate is capped so the fee never falls below dust; only claim the full
	// percentage when the cap did not bite.
	const fullRebate = bidRebate > 0 && bidRebate === Math.floor((priceSats * BID_REBATE_BPS) / 10_000)
	const validFee =
		(!isNat || Number.isInteger(feeRate)) &&
		Number.isFinite(feeRate) &&
		feeRate >= 1 &&
		feeRate <= 500
	// The settlement budget the API funds: the sale transaction plus the 41-vbyte
	// DIESEL mint output every offer transaction carries (offer_mint_vbytes).
	const networkFee = validFee ? Math.ceil(((isItem ? 330 : 380) + OFFER_MINT_VBYTES) * feeRate) : 0
	const total = (priceSats + marketFee + networkFee) * (validQuantity ? quantity : 0)
	const validTrait =
		scope !== 'trait' ||
		(trait &&
			(attributes as Trait[]).some(
				(g) => g.trait_type === trait.type && g.values.some((v) => v.value === trait.value)
			))
	const terms = isNat ? natTerms(tokenAmount, priceSats) : null
	const valid =
		(!isNat || (!!terms && natEnabled)) &&
		priceSats >= MIN_OFFER_SATS &&
		validFee &&
		!feesUnavailable &&
		!itemPending &&
		validTrait &&
		validQuantity &&
		Number.isSafeInteger(total)
	const busy = step !== null && step !== 'done'
	const usd = offerUsd(priceSats, exchangeRate) || 'USD unavailable'

	// Safari's keyboard and pinch zoom change the visual viewport independently
	// of the layout viewport. Keep the close button, form and footer reachable.
	useEffect(() => {
		if (!open || connectingWallet || !window.visualViewport) return
		const viewport = window.visualViewport
		const syncViewport = () => {
			const style = dialogRef.current?.style
			style?.setProperty('--offer-viewport-width', `${viewport.width}px`)
			style?.setProperty('--offer-viewport-height', `${viewport.height}px`)
			style?.setProperty('--offer-center-x', `${viewport.offsetLeft + viewport.width / 2}px`)
			style?.setProperty('--offer-center-y', `${viewport.offsetTop + viewport.height / 2}px`)
		}
		const frame = requestAnimationFrame(syncViewport)
		viewport.addEventListener('resize', syncViewport)
		viewport.addEventListener('scroll', syncViewport)
		return () => {
			cancelAnimationFrame(frame)
			viewport.removeEventListener('resize', syncViewport)
			viewport.removeEventListener('scroll', syncViewport)
		}
	}, [open, connectingWallet])

	useEffect(() => {
		statusRef.current?.scrollIntoView({ block: 'nearest' })
		progressRef.current?.scrollIntoView({ block: 'nearest' })
	}, [error, step, currentOffer])

	useEffect(() => {
		const resume = () => setConnectingWallet(false)
		event.listen('connect-wallet-closed', resume)
		return () => event.removeListener('connect-wallet-closed', resume)
	}, [])

	useEffect(
		() => () => {
			if (validationTimer.current) clearTimeout(validationTimer.current)
			operation.current?.abort()
		},
		[]
	)

	function placed(offer: Offer, notify = true) {
		const slug = offer.collection_slug || collectionSlug
		if (slug) {
			void mutate(
				['offers:collection', slug],
				(current: import('@/utils/offers').CollectionOffers | undefined) =>
					seedConfirmedCollectionOffer(current, offer, slug),
				{ revalidate: true }
			)
		}

		// Seed the exact keys even if quick view unmounted its item panel during signing.
		// The receipt is server-confirmed; normal polling/focus refresh remains authoritative.
		if (offer.inscription_id) {
			void mutate(
				['offers:item', offer.inscription_id],
				(current: { offers: Offer[]; collection_offers: Offer[] } | undefined) => ({
					offers: [offer, ...(current?.offers || []).filter((o) => o.id !== offer.id)],
					collection_offers: current?.collection_offers || []
				}),
				{ revalidate: false }
			)
		}
		if (offer.buyer_address) {
			void mutate(
				['offers:incoming', offer.buyer_address],
				(current: { received: Offer[]; sent: Offer[] } | undefined) => ({
					received: current?.received || [],
					sent: [offer, ...(current?.sent || []).filter((o) => o.id !== offer.id)]
				}),
				{ revalidate: false }
			)
		}
		if (notify)
			toast({
				title: 'Offer placed successfully',
				variant: 'success',
				duration: 8000,
				description: `${fmtBtc(offer.price_sats || priceSats)} BTC${
					offerUsd(offer.price_sats || priceSats, exchangeRate)
						? ` (${offerUsd(offer.price_sats || priceSats, exchangeRate)})`
						: ''
				} on ${name || (isItem ? 'this item' : collectionSlug)}. You can manage it from your wallet.`
			})
		if (!operation.current?.signal.aborted) setPlacedOffer(offer)
		onPlaced?.(offer)
		// The note goes to the collection's Social feed as a thesis post, so the
		// whole market sees the reasoning, not one seller in private.
		const postSlug = collectionSlug || offer.collection_slug
		const tag = postSlug ? collectionSmartTag(postSlug) : null
		const note = socialPostOn ? message.trim() : ''
		if (note && tag && !socialPosted.current) {
			socialPosted.current = true
			social.submit(
				`${note}\n\nOffer: ${fmtBtc(offer.price_sats || priceSats)} BTC on ${name || postSlug}`,
				[tag],
				null,
				null,
				tag
			)
		}
	}

	/** One notification for a whole batch: how many are live, at what price, in total. */
	function batchToast(offers: Offer[], of: number) {
		const count = offers.length
		if (!count) return
		const each = offers[0]?.price_sats || priceSats
		const total = offers.reduce((sum, o) => sum + (o.price_sats || priceSats), 0)
		const usd = offerUsd(total, exchangeRate)
		const complete = count === of
		toast({
			title: complete ? `${count} offers placed` : `${count} of ${of} offers placed`,
			variant: complete ? 'success' : 'default',
			duration: 10000,
			description: `${count} × ${fmtBtc(each)} BTC on ${name || collectionSlug}, ${fmtBtc(total)} BTC${
				usd ? ` (${usd})` : ''
			} in total. ${
				complete
					? 'You can manage them from your wallet.'
					: 'The rest were not placed; these are live in your wallet.'
			}`
		})
	}

	async function submit(evt: React.FormEvent) {
		evt.preventDefault()
		if (submitting.current || step === 'done' || batchStopped) return
		if (!loggedIn) {
			setConnectingWallet(true)
			event.emit('command', { id: 'connect-wallet' })
			return
		}
		if (!valid) {
			setAttempted(true)
			if (validationTimer.current) clearTimeout(validationTimer.current)
			validationTimer.current = setTimeout(() => {
				setAttempted(false)
				validationTimer.current = null
			}, 3000)
			if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
				submitRef.current?.animate(
					[
						{ transform: 'translateX(0)' },
						{ transform: 'translateX(-5px)' },
						{ transform: 'translateX(5px)' },
						{ transform: 'translateX(-4px)' },
						{ transform: 'translateX(4px)' },
						{ transform: 'translateX(0)' }
					],
					{ duration: 320 }
				)
			}
			const invalidId =
				priceSats < MIN_OFFER_SATS
					? 'make-offer-price'
					: !validQuantity
					? 'offer-quantity'
					: !validTrait
					? 'offer-trait-search'
					: 'offer-custom-fee'
			if (invalidId === 'offer-custom-fee' && feesUnavailable) {
				switchToCustomFee()
				return
			}
			const field = document.getElementById(invalidId)
			field?.focus({ preventScroll: true })
			field?.scrollIntoView({ block: 'nearest', behavior: 'auto' })
			return
		}
		setError(null)
		setItemMoved(false)
		batchPlaced.current = []
		setPlacedCount(0)
		submitting.current = true
		const controller = new AbortController()
		operation.current = controller
		const telemetry = beginOfferFlow('offer_make', surface, {
			scope,
			collectionSlug: isItem ? undefined : collectionSlug,
			priceSats,
			feeRate,
			role: 'buyer'
		})
		const updateStep = (next: OfferStep) => {
			if (!controller.signal.aborted) setStep(next)
			if (OFFER_STEP_STAGES[next]) telemetry.stage(OFFER_STEP_STAGES[next])
		}
		try {
			const params = {
				scope,
				inscriptionId: isItem ? inscriptionId : undefined,
				collectionSlug: isItem ? undefined : collectionSlug,
				traitType: scope === 'trait' ? trait?.type : undefined,
				traitValue: scope === 'trait' ? trait?.value : undefined,
				natTerms: terms ?? undefined,
				priceSats,
				feeRate,
				validityDays: days,
				message: undefined
			}
			if (quantity > 1) {
				await makeOfferBatch(params, quantity, {
					onStep: (next, current) => {
						if (controller.signal.aborted) return
						updateStep(next)
						setCurrentOffer(current)
					},
					onPlaced: (offer) => {
						batchPlaced.current.push(offer)
						setPlacedCount(batchPlaced.current.length)
						placed(offer, false)
					},
					signal: controller.signal
				})
				batchToast(batchPlaced.current, quantity)
			} else {
				const offer = await makeOffer(params, updateStep, undefined, controller.signal)
				placed(offer)
			}
			telemetry.success()
		} catch (e: unknown) {
			telemetry.failure(e, {
				offerId: e instanceof OfferBatchError ? e.placed[e.placed.length - 1]?.id : undefined
			})
			// A stopped or cancelled batch still says what went live.
			if (quantity > 1) batchToast(batchPlaced.current, quantity)
			if (controller.signal.aborted) return
			setStep(null)
			if (e instanceof OfferBatchError) setBatchStopped(true)
			setItemMoved(e instanceof OfferError && e.code === 'offer_item_moved')
			setError(offerFailureMessage(e))
		} finally {
			submitting.current = false
		}
	}

	return (
		<Dialog.Root
			open={open && !connectingWallet}
			// While the wallet is asked to sign, the native Ordinals Wallet unlock
			// renders as its own modal outside this one. A modal dialog makes the
			// rest of the page inert (pointer-events off, aria-hidden), so on
			// touch devices the password field could not even be tapped: drop
			// the focus trap and pointer lock for the duration of the flow.
			modal={!busy}
			onOpenChange={(next) => {
				if (!next && !busy) close()
			}}
		>
			<Dialog.Portal>
				{/* Radix renders no overlay for a non-modal dialog, so keep our own
				    backdrop up while the wallet is signing. */}
				{busy ? (
					<div className={styles.overlay} aria-hidden />
				) : (
					<Dialog.Overlay className={styles.overlay} />
				)}
				<Dialog.Content
					className={`${styles.dialog} ${styles.bidDialog}`}
					ref={dialogRef}
					onOpenAutoFocus={(e) => {
						e.preventDefault()
						if (!returnFocusRef.current)
							returnFocusRef.current = document.activeElement as HTMLElement
						if (window.matchMedia('(pointer: coarse), (max-width: 600px)').matches) {
							titleRef.current?.focus({ preventScroll: true })
						} else {
							inputRef.current?.focus({ preventScroll: true })
						}
					}}
					onCloseAutoFocus={(e) => {
						e.preventDefault()
						returnFocusRef.current?.focus()
					}}
					onPointerDownOutside={(e) => {
						if (busy) e.preventDefault()
					}}
				>
					<form noValidate onSubmit={submit} aria-busy={busy} className={styles.form}>
						<header className={styles.header}>
							<div className={styles.bidHeaderMain}>{!isItem && <div className={styles.bidIdentity}>{collectionIcon}<span>{name || collectionSlug}</span></div>}
							<Dialog.Title ref={titleRef} tabIndex={-1}>
								{isNat ? 'Make DMT-NAT offer' : isItem ? 'Make item offer' : 'Make collection offer'}
							</Dialog.Title></div>
							{!isNat && <span className={styles.rebatePill}>{BID_REBATE_PERCENT}% Fee Rebate ({exchangeRate > 0 ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((bidRebate * (validQuantity ? quantity : 1) / 1e8) * exchangeRate) : 'USD unavailable'})</span>}
							<Dialog.Description className="sr-only">
								{isNat
									? 'Offer on one exact-size DMT-NAT transfer lot.'
									: isItem
									? `Offer on ${name || 'this inscription'}.`
									: `Offer on ${name || collectionSlug} or a specific trait.`}{' '}
								Funds are escrowed when you place an offer.
							</Dialog.Description>
							<Dialog.Close aria-label="Close offer" className={styles.close}>
								<X size={18} />
							</Dialog.Close>
						</header>
						<div className={styles.body}>
							{step === 'done' ? (
								<OfferSuccessReceipt
									offer={placedOffer}
									quantity={quantity}
									days={days}
									name={name || collectionSlug || 'Bitcoin inscription'}
									inscriptionId={inscriptionId}
									collectionSlug={collectionSlug}
									preview={receiptPreview || collectionIcon}
									onClose={close}
								/>
							) : (
								<>
									{isItem ? (
										itemPreview
									) : (
										<div className={styles.collectionRow}>

											{!isNat && (
												<div className={styles.stats}>
													<button type="button" disabled={busy || batchStopped || !validReferencePrice(bidRef.sats)} onClick={() => { if (validReferencePrice(bidRef.sats)) { setEnteredAmount(offerInputBtc(bidRef.sats)); setAmountCurrency('BTC') } }}>
														Floor price: <b>{bidRef.sats ? fmtBtc(bidRef.sats) : '—'}</b>{' '}
														<Bitcoin className={styles.bitcoin} size={14} aria-hidden="true" />
														<span className="sr-only">BTC</span>
														<OfferUsd sats={bidRef.sats} compact />
													</button>
													<button type="button" disabled={busy || batchStopped || !validReferencePrice(bidRef.floorSats)} onClick={() => { if (validReferencePrice(bidRef.floorSats)) { setEnteredAmount(offerInputBtc(bidRef.floorSats)); setAmountCurrency('BTC') } }}>
														Lowest listing:{' '}
														<b>{bidRef.floorSats ? fmtBtc(bidRef.floorSats) : '—'}</b>{' '}
														<Bitcoin className={styles.bitcoin} size={14} aria-hidden="true" />
														<span className="sr-only">BTC</span>
														<OfferUsd sats={bidRef.floorSats} compact />
													</button>
													<button type="button" disabled={busy || batchStopped || !validReferencePrice(reference?.topOfferSats)} onClick={() => { if (validReferencePrice(reference?.topOfferSats)) { setEnteredAmount(offerInputBtc(reference?.topOfferSats)); setAmountCurrency('BTC') } }}>
														Top offer:{' '}
														<b>{reference?.topOfferSats ? fmtBtc(reference.topOfferSats) : '—'}</b>{' '}
														<Bitcoin className={styles.bitcoin} size={14} aria-hidden="true" />
														<span className="sr-only">BTC</span>
														<OfferUsd sats={reference?.topOfferSats} compact />
													</button>
												</div>
											)}
										</div>
									)}
									<fieldset disabled={busy || batchStopped} className={styles.fields}>
										{isNat && (
											<>
												<div className={styles.tokenAmountRow}>
													<div>
														<label htmlFor="nat-unit-price">Price (sats/NAT)</label>
														<input
															ref={inputRef}
															id="nat-unit-price"
															inputMode="decimal"
															autoComplete="off"
															placeholder="0.0001"
															value={unitPrice}
															onChange={(e) => setUnitPrice(e.target.value)}
														/>
													</div>
													<div>
														<label htmlFor="nat-lot">DMT-NAT amount</label>
														<input
															id="nat-lot"
															inputMode="numeric"
															autoComplete="off"
															placeholder="Enter amount"
															value={tokenAmount}
															onChange={(e) => setTokenAmount(e.target.value.replace(/,/g, ''))}
														/>
													</div>
												</div>
												<div className={styles.bidSummary}>
													<span>
														{tokenAmount && /^\d+$/.test(tokenAmount)
															? `${formatNatAmount(tokenAmount)} NAT`
															: 'Exact-size transfer lot'}
													</span>
													<strong>
														{fmtBtc(priceSats)} BTC <OfferUsd sats={priceSats} />
													</strong>
												</div>
												{tokenAmount && unitPrice && !priceSats && (
													<p role="status" className={styles.error}>
														Enter a whole NAT amount and a price whose total is a whole number of
														satoshis.
													</p>
												)}
												<p className={styles.hint}>
													One offer buys this exact amount in one transfer. No partial fills.
												</p>
												{!natEnabled && (
													<p role="status" className={styles.hint}>
														NAT funding is temporarily unavailable. No funds will be sent.
													</p>
												)}
											</>
										)}
										{!isNat && (
											<div className={isItem ? styles.itemAmount : styles.amountRow}>
												<div>
													<div className={styles.amountLabelRow}><label htmlFor="make-offer-price">
														{isNat ? 'Total offer (BTC)' : isItem ? 'Your Offer' : 'Offer Amount'}
														{!isItem && quantity > 1 && ' · per item'}
													</label><span className={styles.mobileMinimum}>Min offer: {MIN_OFFER_BTC}</span></div>
													<div className={`${styles.amountInput} ${styles.currencyInput}`}>
														<span className={styles.currencyPrefix} aria-hidden="true">{amountCurrency === 'BTC' ? <BitcoinIcon /> : '$'}</span>
														<input
															ref={inputRef}
															id="make-offer-price"
															aria-label={`Offer amount in ${amountCurrency}`}
															type="text"
															inputMode="decimal"
															autoComplete="off"
															placeholder="0"
															value={amountCurrency === 'USD' ? usdDraft : raw}
															onChange={(e) => {
																const value = e.target.value
																if (amountCurrency === 'BTC') setEnteredAmount(value)
																else {
																	setUsdDraft(value)
																	setEnteredAmount(/^\d*\.?\d*$/.test(value) && exchangeRate > 0 && value ? (Number(value) / exchangeRate).toFixed(8) : '')
																}
															}}
															aria-describedby="offer-amount-help"
															aria-invalid={attempted && priceSats < MIN_OFFER_SATS}
														/>
<div className={styles.currencyHelp}>

														<button type="button" aria-label={`Switch amount to ${amountCurrency === 'BTC' ? 'USD' : 'BTC'}`} disabled={!(exchangeRate > 0)} onClick={() => {
															if (amountCurrency === 'BTC') setUsdDraft(priceSats ? ((priceSats / 1e8) * exchangeRate).toFixed(2) : '')
															setAmountCurrency(amountCurrency === 'BTC' ? 'USD' : 'BTC')
														}}><ArrowUpDown size={11} /><span>{amountCurrency === 'BTC' ? usd.replace(/^≈\s*/, '') : `${fmtBtc(priceSats)} BTC`}</span></button>
													</div>

													</div>

												</div>
												{!isItem && !isNat && (
													<div>
														<label htmlFor="offer-quantity">Quantity</label>
														<input
															id="offer-quantity"
															aria-invalid={attempted && !validQuantity}
															type="number"
															min={1}
															max={MAX_OFFER_QUANTITY}
															step={1}
															inputMode="numeric"
															value={quantityRaw}
															onChange={(e) => setQuantityRaw(e.target.value)}
															aria-describedby="offer-amount-help"
														/>
													</div>
												)}
											</div>
										)}
										{!isNat && (
											<div className={styles.amountTools}><OfferPriceShortcuts
												value={raw}
												sats={referenceSats}
												label={referenceLabel}
												onChange={(value) => { setEnteredAmount(value); setAmountCurrency('BTC') }}
											/><p
											id="offer-amount-help"
											className={
												attempted && priceSats < MIN_OFFER_SATS ? styles.error : styles.hint
											}
										>
											{`Min offer: ${MIN_OFFER_BTC}`}
										</p></div>
										)}
										{isNat && (<p
											id="offer-amount-help"
											className={
												attempted && priceSats < MIN_OFFER_SATS ? styles.error : styles.hint
											}
										>
											{`Min offer: ${MIN_OFFER_BTC}`}
										</p>)}
										{!isItem && quantity > 1 && validQuantity && (
											<p className={styles.hint}>
												{quantity} separate offers · {quantity * 2} wallet signatures. Each offer is
												funded in turn from the same coins; the batch stops if an offer fails.
											</p>
										)}
										{!isItem && !isNat && (
											<section className={styles.section}>
												<h3>Offer Type</h3><div className={styles.scopeChoices}>
												<label className={styles.radioRow}>
													<input
														type="radio"
														name="offer-scope"
														checked={scope === 'collection'}
														onChange={() => setScope('collection')}
													/>
													<div className={styles.radioCopy}>
														<strong>Any item</strong>

													</div>
												</label>
												<label className={styles.radioRow}>
													<input
														type="radio"
														name="offer-scope"
														checked={scope === 'trait'}
														onChange={() => setScope('trait')}
													/>
													<div className={styles.radioCopy}>
														<strong>Specific trait</strong>

													</div>
												</label>
											</div></section>
										)}
										{!isItem && !isNat && (
											<>
												{scope === 'trait' && (
													<div className={styles.traits}>
														{attempted && !validTrait && (
															<p className={styles.error}>Choose a trait for this offer.</p>
														)}
														<input
															id="offer-trait-search"
															aria-label="Search traits"
															aria-invalid={attempted && !validTrait}
															placeholder="Search traits..."
															type="search"
															value={search}
															onChange={(e) => setSearch(e.target.value)}
														/>
														<p className={styles.hint}>
															{trait ? `Selected: ${trait.type} / ${trait.value}. ` : ''}Choose one
															trait per offer. Selecting another replaces it.
														</p>
													</div>
												)}
												<div
													className={`${styles.section} ${
														scope === 'trait' ? styles.traitExpiryGrid : ''
													}`}
												>
													{scope === 'trait' && (
														<section className={styles.traitColumn}>
															<h3>Traits</h3>
															{traitsLoading && (
																<p role="status" className={styles.hint}>
																	Loading traits…
																</p>
															)}
															{traitsError && (
																<p role="alert" className={styles.error}>
																	Traits could not be loaded. Reopen this form to retry.
																</p>
															)}
															{!traitsLoading && !traitsError && !groups.length && (
																<p className={styles.hint}>No matching traits.</p>
															)}
															{groups.map((group) => (
																<details
																	className={styles.traitGroup}
																	key={`${group.trait_type}-${!!search}`}
																	open={search ? true : undefined}
																>
																	<summary>
																		<strong>{group.trait_type}</strong>
																		<span>{group.values.length} traits</span>
																	</summary>
																	<div className={styles.traitValues}>
																		{group.values.map((value) => {
																			const top = Math.max(
																				0,
																				...traitOffers
																					.filter(
																						(o) =>
																							o.trait_type === group.trait_type &&
																							o.trait_value === value.value
																					)
																					.map((o) => o.price_sats)
																			)
																			return (
																				<label key={value.value} className={styles.traitValue}>
																					<input
																						type="checkbox"
																						checked={
																							trait?.type === group.trait_type &&
																							trait?.value === value.value
																						}
																						onChange={(e) =>
																							setTrait(
																								e.target.checked
																									? { type: group.trait_type, value: value.value }
																									: null
																							)
																						}
																					/>
																					<strong>{value.value}</strong>
																					<span>
																						{value.count.toLocaleString()}
																						{supply
																							? ` (${((value.count / supply) * 100).toFixed(1)}%)`
																							: ''}
																					</span>
																					<span className={top ? styles.topOffer : undefined}>
																						{top ? (
																							<>
																								Top: {fmtBtc(top)}{' '}
																								<Bitcoin
																									className={styles.bitcoin}
																									size={14}
																									aria-hidden="true"
																								/>
																								<span className="sr-only">BTC</span>
																								<OfferUsd sats={top} />
																							</>
																						) : (
																							'No offers yet'
																						)}
																					</span>
																				</label>
																			)
																		})}
																	</div>
																</details>
															))}
														</section>
													)}
												</div>
											</>
										)}
										{isNat && (
											<div className={styles.fundingCard}>
												<div>
													<strong>Funded offer</strong>
													<p className={styles.hint}>
														BTC is reserved for this offer. Sellers can accept while you’re offline.
														Cancel to refund unspent funds, less the Bitcoin network fee.
													</p>
												</div>
											</div>
										)}
										{socialPostOn && (
											<div className={styles.messageField}>
												<label htmlFor="offer-message">
													Post to Social <span>(optional)</span>
												</label>
												<textarea
													id="offer-message"
													rows={2}
													value={message}
													onChange={(e) =>
														setMessage(
															Array.from(e.target.value).slice(0, MAX_OFFER_MESSAGE_LENGTH).join('')
														)
													}
													placeholder="Why are you making this offer? Share your thesis with the collection’s feed…"
													aria-describedby="offer-message-hint offer-message-count"
												/>
												<div className={styles.messageHint}>
													<span id="offer-message-hint">
														Posts to the collection’s Social feed as a thesis with your offer
														attached. Public — everyone can read it.
													</span>
													<span id="offer-message-count">
														{Array.from(message).length}/{MAX_OFFER_MESSAGE_LENGTH}
													</span>
												</div>
											</div>
										)}
										<details className={styles.feeDetails} ref={feeDetailsRef}>
											<summary>
												Fees & funding details
												<span>
													{priceSats > 0
														? feesUnavailable
															? 'Escrow: — BTC'
															: `Escrow: ${fmtBtc(total)} BTC`
														: 'Review before signing'}
													{priceSats > 0 && !feesUnavailable && <OfferUsd sats={total} />}
												</span>
											</summary>
											<div className={styles.feeContent}>
												<div className={styles.invoiceFeeHeader}>
 <span>Network fee</span>
 <button type="button" className={styles.customFeeToggle} onClick={() => setSpeed(speed === 'custom' ? 'normal' : 'custom')}>Custom</button>
</div>
<div className={styles.invoiceFeeOptions}>
 {([{ key: 'slow', label: 'Slow' }, { key: 'normal', label: 'Medium' }, { key: 'fast', label: 'Fast' }] as const).map(option => (
  <div key={option.key} className={styles.invoiceFeeOption}>
   <SelectButton selected={speed === option.key} onSelect={() => setSpeed(option.key)} />
   <button type="button" aria-pressed={speed === option.key} onClick={() => setSpeed(option.key)}>
    <span>{option.label}</span><small>{feesLoading || feesFallback ? '— sats/vB' : `${speeds[option.key]} sats/vB`}</small>
   </button>
  </div>
 ))}
</div>
{speed === 'custom' && (
													<>
														<label htmlFor="offer-custom-fee">Custom network fee</label>
														<input
															id="offer-custom-fee"
															aria-invalid={attempted && !validFee}
															type="number"
															min="1"
															max="500"
															step="any"
															value={customFee}
															onChange={(e) => setCustomFee(e.target.value)}
														/>
														{!validFee && (
															<p className={attempted ? styles.error : styles.hint}>
																Enter 1–500 sat/vB.
															</p>
														)}
													</>
												)}
												<dl className={styles.breakdown}>
													<div>
														<dt>
															{quantity > 1
																? `Offers (${quantity} items)`
																: 'Offer amount'}
														</dt>
														<dd>
															{fmtBtc(priceSats * (validQuantity ? quantity : 0))} BTC{' '}
															<OfferUsd sats={priceSats * (validQuantity ? quantity : 0)} />
														</dd>
													</div>
													<div>
														<dt>
															Marketplace fee{fullMarketFee !== 1000 && ` (${MARKET_FEE_PERCENTAGE}%)`}
</dt>
														<dd>
															{fmtBtc(fullMarketFee * (validQuantity ? quantity : 0))} BTC{' '}
															<OfferUsd sats={fullMarketFee * (validQuantity ? quantity : 0)} />
														</dd>
													</div>
													<div data-testid="bid-rebate-line">
														<dt>Fee rebate{fullRebate && ` (${BID_REBATE_PERCENT}% of offer)`}</dt>
														<dd className={styles.rebate}>
															−{fmtBtc(bidRebate * (validQuantity ? quantity : 0))} BTC{' '}
															<OfferUsd
																sats={bidRebate * (validQuantity ? quantity : 0)}
																parentheses
															/>
														</dd>
													</div>
													<div>
														<dt>Settlement fee</dt>
														<dd>
															{feesUnavailable ? (
																'— BTC'
															) : (
																<>
																	{fmtBtc(networkFee * (validQuantity ? quantity : 0))} BTC{' '}
																	<OfferUsd sats={networkFee * (validQuantity ? quantity : 0)} />
																</>
															)}
														</dd>
													</div>
													<div>
														<dt>Escrow</dt>
														<dd>
															{feesUnavailable ? (
																'— BTC'
															) : (
																<>
																	{fmtBtc(total)} BTC <OfferUsd sats={total} />
																</>
															)}
														</dd>
													</div>
												</dl>
												<p className={styles.hint}>
													The funding transaction&apos;s own network fee is paid from your wallet on top of the escrow;
													your wallet shows it when you sign. If you cancel, the escrow comes back minus a network fee and
													no marketplace fee is charged.
												</p>
											</div>
										</details>
										{isItem && (
											<p className={styles.hint}>
												Funds are escrowed on-chain when you place an offer. It stays live for up
												to a year unless it fills or you cancel it. An expired or declined offer is
												not refunded automatically: cancel it to get your BTC back.
											</p>
										)}
									</fieldset>
									{feeEstimatesLoading && !busy && (
										<p role="status" className={styles.hint} data-testid="offer-fee-loading">
											Loading fee estimates…
										</p>
									)}
									{feeEstimatesMissing && !busy && (
										<p role="alert" className={styles.error} data-testid="offer-fee-unavailable">
											Fee estimates are unavailable right now. Enter a custom fee rate to continue.{' '}
											<button type="button" className={styles.customFeeToggle} onClick={switchToCustomFee}>
												Enter a custom fee rate
											</button>
										</p>
									)}
									{itemPending && !busy && (
										liveItemError ? (
											<p role="alert" className={styles.error} data-testid="offer-item-unavailable">
												{liveItemError instanceof Error
													? liveItemError.message
													: 'The item’s current output could not be checked. Try again.'}{' '}
												<button type="button" className={styles.customFeeToggle} onClick={() => void reloadLiveItem()}>
													Retry
												</button>
											</p>
										) : (
											<p role="status" className={styles.hint} data-testid="offer-item-loading">
												Checking the item’s current output…
											</p>
										)
									)}
									{error && (
										<p ref={statusRef} role="alert" className={styles.error}>
											{error}
											{itemMoved && (
												<>
													{' '}
													<button type="button" className={styles.customFeeToggle} onClick={refreshItem}>
														Refresh item
													</button>
												</>
											)}
										</p>
									)}
									{quantity > 1 && (busy || batchStopped) ? (
										<div ref={progressRef} className={styles.batchProgress} role="status" aria-live="polite">
											<div className={styles.batchHead}>
												<span>
													{batchStopped
														? `${placedCount} of ${quantity} offers placed`
														: `Placing offer ${Math.min(currentOffer, quantity)} of ${quantity}`}
												</span>
												<span className={styles.batchCount}>{placedCount} placed</span>
											</div>
											<div
												className={styles.batchBar}
												role="progressbar"
												aria-label="Offers placed"
												aria-valuemin={0}
												aria-valuemax={quantity}
												aria-valuenow={placedCount}
											>
												{Array.from({ length: quantity }, (_, i) => {
													const state =
														i < placedCount
															? 'placed'
															: i === placedCount && busy
															? 'active'
															: i === placedCount && batchStopped
															? 'failed'
															: 'pending'
													return (
														<span
															key={i}
															data-state={state}
															style={
																state === 'active'
																	? ({ '--fill': STEP_FRACTION[step ?? 'building'] } as React.CSSProperties)
																	: undefined
															}
														/>
													)
												})}
											</div>
											{busy && step && (
												<p className={styles.batchStep}>
													{STEP_COPY[step]}
													{(step === 'sign_funding' || step === 'sign_offer') && (
														<WalletWaitHint key={`${currentOffer}:${step}`} />
													)}
												</p>
											)}
										</div>
									) : (
										busy &&
										step !== 'building' && (
											<p ref={statusRef} role="status" className={styles.progress}>
												{STEP_COPY[step!]}
												{(step === 'sign_funding' || step === 'sign_offer') && (
													<WalletWaitHint key={`${currentOffer}:${step}`} />
												)}
											</p>
										)
									)}
								</>
							)}
						</div>
						<footer className={styles.footer}>
							{busy && (
								<>
									<p className={styles.hint}>
										{step === 'activating'
											? 'This offer is being submitted. Closing won’t cancel it.'
											: quantity > 1
											? 'Cancel stops the remaining offers. Offers already placed stay active.'
											: 'Cancel stops this offer. You can dismiss the wallet window too.'}
									</p>
									<button type="button" className={styles.cancel} onClick={close}>
										{step === 'activating' ? 'Close' : 'Cancel'}
									</button>
								</>
							)}

							{step === 'done' || batchStopped ? (
								<button type="button" className={styles.submit} onClick={close}>
									{batchStopped ? 'Close' : 'Done'}
								</button>
							) : (
								<button
									type="submit"
									className={styles.submit}
									ref={submitRef}
									data-invalid={attempted && !valid}
									disabled={busy || (isNat && !natEnabled) || (loggedIn && (feesUnavailable || itemPending))}
								>

									{isNat && !natEnabled
										? 'NAT funding unavailable'
										: busy
										? <span className="flex items-center justify-center" role="status" aria-label="Processing offer"><Loading className="!w-5 !h-5" /></span>
										: attempted && quantity > MAX_OFFER_QUANTITY
										? `Max Quantity: ${MAX_OFFER_QUANTITY}`
										: !loggedIn
										? 'Connect wallet'
										: quantity > 1 && validQuantity
										? `Make ${quantity} Offers`
										: isNat
										? 'Make DMT-NAT offer'
										: <PlaceBidLabel />}
								</button>
							)}
						</footer>
					</form>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
